<!-- @format -->

<template>
	<div class="add-auth-people-from-dnb">
		<div
			v-if="items.length === 0"
			class="add-auth-people-from-dnb__empty"
		>
			{{ $t('§eq_operator.dnb_no_principals_available') }}
		</div>

		<template v-else>
			<div class="add-auth-people-from-dnb__progress">
				<p class="add-auth-people-from-dnb__progress-text">
					{{
						$t('§eq_operator.dnb_progress_summary', {
							resolved: resolvedCount,
							total: actionableCount,
							remaining: remainingCount,
						})
					}}
				</p>
				<ProgressBar
					:value="progressPercent"
					:showValue="false"
				/>
			</div>

			<Accordion
				v-model:value="activePanel"
				class="mb-3"
			>
				<DnbPrincipalAccordionItem
					v-for="(item, i) in items"
					:key="item.id"
					:principal="item.principal"
					:value="String(i)"
					:matches="item.matches"
					:state="item.state"
					:countryIsoByCountryId="countryIsoByCountryId"
					:countryLabelByCountryId="countryLabelByCountryId"
					@update:state="(s) => replaceState(i, s)"
					@dismiss="onDismiss(i)"
					@undismiss="onUndismiss(i)"
					@confirm-match="onConfirmMatchDone(i)"
					@save-as-new="onConfirmMatchDone(i)"
					@re-open="onReOpen(i)"
					@undo-match="onUndoMatch(i)"
				/>
			</Accordion>
		</template>

		<div class="flex justify-start gap-2">
			<CoreButton
				v-if="items.length > 0"
				icon="pi-save"
				label="§ui_button.save_all"
				:disabled="!canSave || savingAll"
				:loading="savingAll"
				@click="handleSaveAll"
			/>
			<CoreButton
				variant="tertiary"
				icon="pi-times"
				label="§ui_button.cancel"
				@click="handleCancel"
			/>
		</div>
	</div>
</template>

<script>
import { parseISO } from 'date-fns'
import Accordion from 'primevue/accordion'
import ProgressBar from 'primevue/progressbar'
import CoreButton from '@/components/core/CoreButton.vue'
import DnbPrincipalAccordionItem from './DnbPrincipalAccordionItem.vue'
import AuthorizedPersonService from '@/api/AuthorizedPersonService.js'
import { useClientStore } from '@/stores/client'
import { useModalStore } from '@/stores/modal'
import { useOptionsStore } from '@/stores/options'
import {
	buildCountryIdByIsoMap,
	deriveNameParts,
	filterIndividuals,
	findMatches,
	toIsoDateOnly,
} from '@/helpers/dnbMatching'
import { isSignatoryResidencyCountry } from '@/helpers/signatoryResidencyCountry'

const SESSION_STORAGE_PREFIX = 'dnb-auth-people-'

function serializeStagedDataDate(stagedData) {
	if (!stagedData) return stagedData
	const { date_of_birth, ...rest } = stagedData
	return {
		...rest,
		date_of_birth: date_of_birth instanceof Date ? date_of_birth.toISOString() : date_of_birth,
	}
}

function deserializeStagedDataDate(stagedData) {
	if (!stagedData) return stagedData
	const { date_of_birth, ...rest } = stagedData
	let parsed = date_of_birth
	if (typeof date_of_birth === 'string' && date_of_birth) {
		try {
			parsed = parseISO(date_of_birth)
		} catch {
			parsed = null
		}
	}
	return { ...rest, date_of_birth: parsed }
}

export default {
	name: 'AddAuthorizedPeopleFromDnbDataModal',

	components: {
		Accordion,
		ProgressBar,
		CoreButton,
		DnbPrincipalAccordionItem,
	},

	data() {
		return {
			items: [],
			savingAll: false,
			hasSaved: false,
			activePanel: null,
			_isUnmounted: false,
		}
	},

	computed: {
		clientId() {
			return this.clientStore?.clientData?.id ?? null
		},

		sessionStorageKey() {
			return this.clientId ? `${SESSION_STORAGE_PREFIX}${this.clientId}` : null
		},

		savedAuthorizedPeople() {
			return (this.modalStore.data?.savedAuthorizedPeople || []).filter((p) => !p.archived)
		},

		rawPrincipals() {
			return this.modalStore.data?.currentPrincipals || []
		},

		countryIsoByCountryId() {
			const map = {}
			this.savedAuthorizedPeople.forEach((p) => {
				if (p.country_id && p.country_iso_3166) {
					map[p.country_id] = String(p.country_iso_3166).toUpperCase()
				}
			})
			return map
		},

		countryLabelByCountryId() {
			const map = {}
			const options = this.optionsStore?.countryOptions || []
			options.forEach((opt) => {
				if (opt.value !== undefined && opt.label !== undefined) {
					map[opt.value] = this.$t(opt.label)
				}
			})
			return map
		},

		resolvedCount() {
			return this.items.filter(
				(i) => i.state.status === 'resolved' || i.state.status === 'saved',
			).length
		},

		dismissedCount() {
			return this.items.filter((i) => i.state.status === 'dismissed').length
		},

		actionableCount() {
			return this.items.length - this.dismissedCount
		},

		remainingCount() {
			return Math.max(0, this.actionableCount - this.resolvedCount)
		},

		progressPercent() {
			if (this.actionableCount === 0) return 0
			return Math.round((this.resolvedCount / this.actionableCount) * 100)
		},

		allResolved() {
			if (this.actionableCount === 0) return false
			return this.items.every(
				(i) =>
					i.state.status === 'resolved' ||
					i.state.status === 'saved' ||
					i.state.status === 'dismissed',
			)
		},

		hasAnyResolved() {
			return this.items.some((i) => i.state.status === 'resolved')
		},

		canSave() {
			return this.hasAnyResolved
		},
	},

	created() {
		this.clientStore = useClientStore()
		this.modalStore = useModalStore()
		this.optionsStore = useOptionsStore()

		this.initializeItems()
	},

	mounted() {
		const firstPending = this.items.findIndex(
			(i) => i.state.status !== 'resolved' && i.state.status !== 'dismissed',
		)
		if (firstPending !== -1) {
			this.activePanel = String(firstPending)
		}
	},

	beforeUnmount() {
		this._isUnmounted = true
	},

	methods: {
		initializeItems() {
			const individuals = filterIndividuals(this.rawPrincipals)
			const restored = this.loadSession()

			this.items = individuals.map((principal, index) => {
				const matches = findMatches(principal, this.savedAuthorizedPeople, {
					countryIsoByCountryId: this.countryIsoByCountryId,
				})

				const state = this.buildInitialState(principal, matches)
				const savedState = restored?.[index]
				if (savedState) {
					const { stagedData: savedStaged, ...savedRest } = savedState
					Object.assign(state, savedRest)
					if (savedStaged) {
						Object.assign(state.stagedData, deserializeStagedDataDate(savedStaged))
					}

					if (
						state.selectedPersonId !== null &&
						!matches.some((m) => m.person.id === state.selectedPersonId)
					) {
						state.selectedPersonId = null
						const staleStatuses = ['resolved', 'resolving-match', 'resolving-new']
						if (staleStatuses.includes(state.status)) {
							state.status = 'pending'
						}
					}
				}

				return {
					id: `dnb-principal-${index}`,
					principal,
					matches,
					state,
				}
			})
		},

		buildInitialState(principal, matches) {
			const selectedPersonId = matches.length === 1 ? matches[0].person.id : null

			const isoFromNationality = Array.isArray(principal.nationality)
				? principal.nationality[0]?.isoAlpha2Code
				: null
			const countryIdByIso = buildCountryIdByIsoMap(this.countryIsoByCountryId)
			const seededCountryId = isoFromNationality
				? (countryIdByIso[String(isoFromNationality).toUpperCase()] ?? null)
				: null

			const { first_name, last_name } = deriveNameParts(principal)

			return {
				status: 'pending',
				selectedPersonId,
				conflictChoices: {},
				stagedData: {
					first_name,
					last_name,
					date_of_birth: principal.birthDate ? parseISO(principal.birthDate) : null,
					country_id: isSignatoryResidencyCountry(seededCountryId) ? seededCountryId : null,
				},
				nameAssignment: null,
				saveError: null,
				_prevStatus: null,
			}
		},

		replaceState(i, newState) {
			const cur = this.items[i].state
			const { stagedData, conflictChoices, ...rest } = newState
			Object.assign(cur, rest)

			// Merge stagedData into the existing object so the Vuelidate-bound reference
			// stays alive. Partial patches (e.g. {first_name: 'x'}) preserve untouched keys.
			if (stagedData) {
				Object.assign(cur.stagedData, stagedData)
			}

			if (conflictChoices !== undefined) {
				cur.conflictChoices = { ...conflictChoices }
			}

			this.persistSession()
		},

		onDismiss(i) {
			const cur = this.items[i].state
			if (cur.status === 'saved') return
			cur._prevStatus = cur.status === 'dismissed' ? cur._prevStatus : cur.status
			cur.status = 'dismissed'
			cur.saveError = null
			this.persistSession()
		},

		onUndismiss(i) {
			const cur = this.items[i].state
			cur.status = cur._prevStatus || 'pending'
			cur._prevStatus = null
			this.persistSession()
		},

		onConfirmMatchDone(i) {
			const cur = this.items[i].state
			cur.status = 'resolved'
			cur.saveError = null
			this.persistSession()
		},

		onReOpen(i) {
			const cur = this.items[i].state
			if (cur.status === 'saved') return
			cur.status = cur.selectedPersonId !== null ? 'resolving-match' : 'resolving-new'
			cur.saveError = null
			this.activePanel = String(i)
			this.persistSession()
		},

		onUndoMatch(i) {
			const item = this.items[i]
			if (item.state.status === 'saved') return
			const freshState = this.buildInitialState(item.principal, item.matches)
			const { stagedData: freshStaged, ...rest } = freshState
			Object.assign(item.state, rest)
			// Mutate stagedData in place to preserve the object reference Vuelidate is bound to.
			Object.keys(item.state.stagedData).forEach((k) => {
				if (!(k in freshStaged)) delete item.state.stagedData[k]
			})
			Object.assign(item.state.stagedData, freshStaged)
			this.activePanel = String(i)
			this.persistSession()
		},

		async handleSaveAll() {
			if (this.savingAll || !this.canSave) return
			this.savingAll = true
			let anyError = false

			for (let i = 0; i < this.items.length; i++) {
				if (this._isUnmounted) return
				const item = this.items[i]
				if (item.state.status !== 'resolved') continue

				item.state.saveError = null
				try {
					await this.saveItem(item)
				} catch (error) {
					anyError = true
					item.state.saveError = this.extractErrorMessage(error)
				}
			}

			if (this._isUnmounted) return
			this.savingAll = false
			this.persistSession()

			if (!anyError) {
				this.clearSession()
				this.modalStore.setClose()
			}
		},

		async saveItem(item) {
			const { stagedData, selectedPersonId } = item.state
			const payload = {
				first_name: stagedData.first_name,
				last_name: stagedData.last_name,
				date_of_birth: toIsoDateOnly(stagedData.date_of_birth),
				country_id: stagedData.country_id,
				client_id: this.clientStore.clientData.id,
			}

			let person
			if (selectedPersonId !== null) {
				const matched = item.matches.find((m) => m.person.id === selectedPersonId)
				payload.id = selectedPersonId
				payload.passport_file_path = matched?.person?.passport_file_path || ''
				payload.id_verified = Boolean(matched?.person?.id_verification_date)
				person = await AuthorizedPersonService.update(payload)
			} else {
				payload.passport_file_path = ''
				person = await AuthorizedPersonService.create(payload)
			}

			item.state.status = 'saved'
			this.hasSaved = true

			try {
				if (selectedPersonId !== null) {
					this.clientStore.updateAuthorizedPerson(person)
				} else {
					this.clientStore.addAuthorizedPerson(person)
				}
			} catch (storeErr) {
				console.warn('DNB save: local store sync failed', storeErr)
			}
		},

		extractErrorMessage(error) {
			const responseMessage = error?.response?.data?.message
			if (responseMessage) return responseMessage
			return this.$t('§eq_operator.dnb_save_error_generic')
		},

		handleCancel() {
			if (this.hasSaved) {
				this.clearSession()
			}
			this.modalStore.setClose()
		},

		persistSession() {
			if (!this.sessionStorageKey) return
			try {
				const payload = this.items.map((item) => {
					const { _prevStatus, ...rest } = item.state
					return {
						...rest,
						_prevStatus,
						stagedData: serializeStagedDataDate(item.state.stagedData),
					}
				})
				sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(payload))
			} catch {
				console.warn('Failed to persist DNB authorized people session data')
			}
		},

		loadSession() {
			if (!this.sessionStorageKey) return null
			try {
				const raw = sessionStorage.getItem(this.sessionStorageKey)
				if (!raw) return null
				const parsed = JSON.parse(raw)
				return Array.isArray(parsed) ? parsed : null
			} catch {
				return null
			}
		},

		clearSession() {
			if (!this.sessionStorageKey) return
			try {
				sessionStorage.removeItem(this.sessionStorageKey)
			} catch {
				console.warn('Failed to clear DNB authorized people session data')
			}
		},
	},
}
</script>

<style>
.add-auth-people-from-dnb__empty {
	padding: 2rem;
	text-align: center;
	color: var(--color-neutral-900);
	border: 1px dashed var(--color-neutral-400);
	border-radius: 6px;
	margin-bottom: 1.5rem;
	background-color: var(--color-neutral-100);
}

.add-auth-people-from-dnb__progress {
	margin-bottom: 1.5rem;
	padding: 1rem 1.25rem;
	background-color: var(--color-neutral-300, var(--color-neutral-400));
	border-radius: 6px;
}

.add-auth-people-from-dnb__progress-text {
	margin: 0 0 0.75rem 0;
	color: var(--color-neutral-900);
	font-size: 1.4rem;
	font-weight: 500;
}

.add-auth-people-from-dnb .p-accordion {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.add-auth-people-from-dnb .p-accordionpanel {
	border: 1px solid var(--color-neutral-300, var(--color-neutral-400));
	border-radius: 6px;
	overflow: hidden;
	background-color: var(--color-white, #fff);
}

.add-auth-people-from-dnb .p-accordionpanel-active {
	border-color: var(--color-primary);
}

.add-auth-people-from-dnb .p-accordionheader {
	padding: 0.85rem 1.25rem;
}
</style>
